import React from 'react';
import { Card, CardContent, Typography, Box, LinearProgress, Grid, Tooltip, useMediaQuery } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { theme } from '../../../theme/DashboardTheme';

interface StatItem {
  label: string;
  value: number | string;
  barColor: string;
}

interface DashboardCardProps {
  heading: string;
  tooltipText: string;
  mainStatValue: number | string;
  mainStatLabel: string;
  progress: number;
  leftStat: StatItem;
  rightStat: StatItem;
  locked?: boolean;
  headerExtra?: React.ReactNode;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  heading,
  tooltipText,
  mainStatValue,
  mainStatLabel,
  progress,
  leftStat,
  rightStat,
  locked = false,
  headerExtra,
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


  return (
    <Card
      sx={{
        borderRadius: '16px',
        boxShadow: '0px 4px 10px rgba(208, 210, 218, 0.37)',
        border: locked ? '2px solid rgba(255, 255, 255, 0.7)' : 'none',
        height: isMobile ? 'auto' : 261,
        minWidth: isMobile ? 'unset' : 275,
        overflow: 'hidden',
        background: locked ? 'rgba(239, 237, 253, 0.8)' : '#FFFFFF',
      }}
    >
      <CardContent>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2.5} sx={{ mb: isMobile ? 3 : 7, mt: 1.5 }}>
          <Typography
            sx={{ fontFamily: 'InterSemiBold', fontSize: { xs: 18, lg: 13, xl: 18 }, fontWeight: 600, color: '#1A1B20' }}
          >
            {heading}
          </Typography>
          <Tooltip
            title={tooltipText}
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  fontFamily: 'InterRegular',
                  fontSize: '10px',
                  lineHeight: '16px',
                  fontWeight: 500,
                  color: '#ffffff',
                },
              },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: '#D0D1D2', cursor: 'pointer' }} />
          </Tooltip>
          {headerExtra}
        </Box>

        {/* Main stat */}
        <Box display="flex" gap={3} alignItems="center" pb={isMobile ? 2 : 4}>
          <Typography
            sx={{
              fontFamily: 'InterRegular',
              fontSize: { xs: 26, lg: 18, xl: 26 },
              fontWeight: 600,
              color: '#11263C',
            }}
          >
            {mainStatValue}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'InterRegular',
              fontSize: { xs: 17, lg: 12, xl: 17 },
              color: '#000000',
              fontWeight: 600,
              lineHeight: '22px',
            }}
          >
            {mainStatLabel}
          </Typography>
        </Box>

        {/* Progress Bar */}
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 19,
            borderRadius: 5,
            width: '100%',
            mb: 3,
            backgroundColor: '#E3DEF7 !important',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#7459D9',
              borderRadius: 5,
            },
          }}
        />

        {/* Bottom stats */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" sx={{ mt: isMobile ? 2 : { xs: 8, lg: 11, xl: 8 } }}>
          {/* Left stat */}
          <Box display="flex" flexDirection="column" alignItems="flex-start" width="100%">
            <Grid container gap={2} alignItems="flex-start">
              <Grid item xs="auto">
                <Box sx={{ width: 20, height: 4, mt: 2, borderRadius: 3, backgroundColor: leftStat.barColor }} />
              </Grid>
              <Grid item xs p={0}>
                <Typography sx={{ fontFamily: 'InterRegular', fontSize: { xs: 14, lg: 11, xl: 14 }, color: '#7459D9', fontWeight: 600 }}>
                  {leftStat.label}
                </Typography>
                <Typography sx={{ fontFamily: 'InterSemiBold', fontSize: { xs: 18, lg: 13, xl: 18 }, color: '#11263C', mt: 0.5 }}>
                  {leftStat.value}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Right stat */}
          <Box display="flex" flexDirection="column" alignItems="flex-end" width="100%">
            <Grid container gap={2} alignItems="flex-start" justifyContent="flex-end" flexWrap="nowrap">
              <Grid item xs="auto">
                <Box sx={{ width: 20, height: 4, mt: 2, borderRadius: 3, backgroundColor: rightStat.barColor }} />
              </Grid>
              <Grid item xs="auto">
                <Typography sx={{ fontFamily: 'InterRegular', fontSize: { xs: 14, lg: 11, xl: 14 }, color: '#7459D9', fontWeight: 600 }}>
                  {rightStat.label}
                </Typography>
                <Typography sx={{ fontFamily: 'InterSemiBold', fontSize: { xs: 18, lg: 13, xl: 18 }, color: '#11263C', mt: 0.5 }}>
                  {rightStat.value}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DashboardCard;
